/**
 * Accesibilidad de los modales (app-dialog, app-confirm-dialog, app-prompt-dialog).
 * Mantiene la pila de modales abiertos para que Esc y Tab afecten solo al de arriba (los de detalle quedan debajo de sus hijos),
 * y resuelve el foco inicial y la trampa de foco. Una sola implementación: no duplicar esta lógica en cada componente.
 */
const stack: symbol[] = [];
let counter = 0;

export const nextDialogId = (prefix: string): string => `${prefix}-${++counter}`;
export const pushDialog = (id: symbol): void => { if (!stack.includes(id)) stack.push(id); };
export const popDialog = (id: symbol): void => { const i = stack.indexOf(id); if (i >= 0) stack.splice(i, 1); };
export const isTopDialog = (id: symbol): boolean => stack[stack.length - 1] === id;

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function focusables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => {
    if (el.closest('[hidden]')) return false;
    const style = getComputedStyle(el);
    return style.display !== 'none' && style.visibility !== 'hidden';
  });
}

/** Foco inicial: lo marcado con `data-autofocus`, si no el primer campo de formulario del cuerpo, si no el primer control, si no el propio panel. */
export function focusInitial(panel: HTMLElement): void {
  const marked = panel.querySelector<HTMLElement>('[data-autofocus]');
  const body = panel.querySelector<HTMLElement>('[data-dialog-body]') ?? panel;
  const field = body.querySelector<HTMLElement>('input:not([disabled]):not([type="hidden"]):not([type="checkbox"]):not([type="radio"]),select:not([disabled]),textarea:not([disabled])');
  (marked ?? field ?? focusables(body)[0] ?? focusables(panel)[0] ?? panel).focus();
}

/** Tab circula dentro del panel; Shift+Tab en el primer control vuelve al último. */
export function trapTab(event: KeyboardEvent, panel: HTMLElement): void {
  const items = focusables(panel);
  if (items.length === 0) { event.preventDefault(); panel.focus(); return; }
  const first = items[0];
  const last = items[items.length - 1];
  const active = document.activeElement;
  if (event.shiftKey && (active === first || active === panel)) { event.preventDefault(); last.focus(); }
  else if (!event.shiftKey && active === last) { event.preventDefault(); first.focus(); }
}
